import * as Sentry from '@sentry/react';

/**
 * Capture an exception in Sentry
 * @param error The error to capture
 * @param context Optional additional context
 */
export const captureException = (error: Error | unknown, context?: Record<string, any>): void => {
  if (error instanceof Error) {
    Sentry.captureException(error, { extra: context });
  } else {
    Sentry.captureException(new Error(String(error)), { extra: context });
  }
};

/**
 * Capture a message in Sentry
 * @param message The message to capture
 * @param level The severity level
 * @param context Optional additional context
 */
export const captureMessage = (
  message: string, 
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, any>
): void => {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
};

/**
 * Set user information for Sentry
 * @param user User information (id, email, username, etc.)
 */
export const setUser = (user: {
  id?: string;
  email?: string;
  username?: string;
  [key: string]: any;
} | null): void => {
  Sentry.setUser(user);
};

/**
 * Add breadcrumb to Sentry
 * @param breadcrumb The breadcrumb to add
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Wrapper function to handle async operations with Sentry error capturing
 * @param operation The async operation to execute
 * @param operationName Name of the operation (for context)
 */
export const withSentryErrorHandling = async <T>(
  operation: () => Promise<T>,
  operationName: string
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    captureException(error, { operationName });
    throw error;
  }
}; 